import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { CustomStepDefinition, LabelType, Options } from 'ngx-slider-v2';
import { MapDateRange } from '../../models/map-date-range';

const COMPONENT_SELECTOR = 'wlm-map-time-step-slider';

@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './map-time-step-slider.component.html',
    styleUrls: ['./map-time-step-slider.component.scss'],
    standalone: false
})
export class MapTimeStepSliderComponent implements OnInit {
  @Input() set currentStep(value: Date) {
    this.currentValue = +value;
  }

  private _mapDateRange: MapDateRange;
  get mapDateRange(): MapDateRange {
    return this._mapDateRange;
  }
  @Input() set mapDateRange(value: MapDateRange) {
    this._mapDateRange = value;

    if (this.mapDateRange) {
      if (this.mapDateRange.steps) {
        const steps: CustomStepDefinition[] = this.mapDateRange.steps.map((step) => ({
          value: +step,
        }));
        this.options.stepsArray = steps;
        this.currentValue = steps.length ? steps[0].value : null;
      }

      this.reinitializeSlider();
    }
  }
  @Output() stepChange = new EventEmitter<Date>();

  currentValue: number;
  options: Options = {
    stepsArray: [],
    translate: (currentDate: number, labelType: LabelType): string => {
      let date = new Date(currentDate);
      if (labelType === LabelType.Floor && this._mapDateRange.startDate) {
        date = this._mapDateRange.startDate;
      }
      if (labelType === LabelType.Ceil && this._mapDateRange.endDate) {
        date = this._mapDateRange.endDate;
      }
      const formatted = this._localization.formatDate(date);
      return formatted;
    },
    showTicks: true,
    customValueToPosition: (...args) => this.customValueToPosition(...args),
    customPositionToValue: (...args) => this.customPositionToValue(...args),
  };
  renderSlider = true;

  constructor(
    private _localization: LocalizationHelperService,
    private _datesHelper: DateHelperService
  ) {}

  ngOnInit(): void {}

  customValueToPosition = (index: number, minValue: number, maxValue: number): number => {
    const step = this.mapDateRange.steps[index];
    if (step === null || typeof step === 'undefined') {
      return null;
    }

    if (this.mapDateRange.steps.length === 1) {
      return 0.5; // If only one element, put it in the middle.
    }

    const { startDate, range } = this.getRange();

    const position = (+step - +startDate) / range;
    return position;
  };

  customPositionToValue = (position: number, minValue: number, maxValue: number) => {
    const { startDate, range } = this.getRange();

    const calculatedStep = position * range + +startDate;

    const steps = this.options.stepsArray.map((step) => step.value);
    const stepIndex = this._datesHelper.getClosestDate(calculatedStep, steps)?.index;

    return stepIndex;
  };

  onValueChange(value: number): void {
    const step = new Date(value);
    this.stepChange.emit(step);
  }

  private getRange() {
    const startDate = this.mapDateRange.startDate ?? this.mapDateRange.steps[0];
    const endDate =
      this.mapDateRange.endDate ?? this.mapDateRange.steps[this.mapDateRange.steps.length - 1];

    const range: number = +endDate - +startDate;

    return {
      startDate,
      endDate,
      range,
    };
  }

  private reinitializeSlider(): void {
    this.renderSlider = false;
    setTimeout(() => {
      this.renderSlider = true;
    });
  }
}
